<template>
  <section class="search-page">
    <div class="container">
      <!-- show placeholder text if user lands on empty search page -->
      <!-- (checks for null or undefined) -->
      <template v-if="!displayResultsPage">
        <div class="search-page__empty">
          <span class="search-page__empty-search">
            <i class="fa-solid fa-magnifying-glass"></i>
          </span>
          <h1>{{ t.startSearching }}</h1>
          <p>{{ t.startSearchingText }}</p>
          <span class="search-page__empty-arrow">
            <i class="fa-solid fa-arrow-up-long"></i>
          </span>
        </div>
      </template>

      <!-- show content only if there is any -->
      <template v-else>
        <!-- header -->
        <div class="search-page__headline-wrapper">
          <h1
            class="search-page__headline title-custom"
            v-html="colorTitle(t.searchResultsTitle)"></h1>
          <p v-if="term" class="search-page__term">
            {{ t.for }} <span>{{ `"${term}"` }}</span>
          </p>
        </div>

        <div class="search-page__tabs" id="scroll-to">
          <!-- tab titles -->
          <div
            ref="linksContainer"
            class="paragraph-tabs__tab-links-container scroll scroll--start">
            <div class="paragraph-tabs__tab-links" @scroll="handleScroll">
              <a
                v-for="tab in searchData"
                :key="tab.id"
                ref="links"
                :class="{ 'paragraph-tabs__tab-link--active': tab.id === 0 }"
                class="paragraph-tabs__tab-link"
                @click="tabActive = tab.id">
                {{ tab.title }} <span>({{ tab.content.length }})</span>
              </a>
            </div>
          </div>
        </div>

        <!-- content -->
        <div class="search-page__content">
          <!-- results -->
          <div
            class="search-page__results paragraph-tabs__field-tabs-item paragraph-tabs__field-items">
            <div class="tabs__results">
              <Transition
                v-for="tab in searchData"
                :key="tab.id"
                name="toggle-height"
                appear>
                <template v-if="tabActive === tab.id">
                  <div class="search-page__result-items">
                    <!-- no results -->
                    <div
                      v-if="activeContent.length === 0"
                      class="search-page__results--empty">
                      {{ t.noResults }}
                    </div>

                    <!-- results in the section -->
                    <template v-else>
                      <template v-for="item in currentPageItems" :key="item.id">
                        <SearchResult :item="item" />
                      </template>

                      <!-- pager -->
                      <nav class="pager" key="pager">
                        <ul
                          class="pager__items"
                          v-if="activeContent.length > itemsPerPage">
                          <li class="pager__item pager__item--back">
                            <a
                              @click="
                                totalPageCount !== 1 && setPage(currentPage - 1)
                              "
                              class="pager__item-link"
                              :class="{
                                'pager__item-link--disabled': currentPage === 0,
                              }">
                            </a>
                          </li>

                          <li
                            v-for="(page, key) in totalPageCount"
                            :key="key"
                            class="pager__item"
                            :class="{
                              'pager__item--first': 0 === key,
                              'pager__item--active': currentPage === key,
                              'pager__item--prev': currentPage - 1 === key,
                              'pager__item--next': currentPage + 1 === key,
                              'pager__item--last': totalPageCount - 1 === key,
                              'pager__item--ellipsis': showEllipsis(
                                currentPage,
                                key,
                              ),
                            }">
                            <div
                              v-if="showEllipsis(currentPage, key)"
                              class="pager__item-link">
                              <span><i class="far fa-ellipsis-h"></i></span>
                            </div>
                            <a
                              @click.prevent="setPage(key)"
                              v-else
                              class="pager__item-link"
                              :class="{
                                'pager__item-link--disabled':
                                  totalPageCount === 1,
                              }">
                              <span>{{ page }}</span>
                            </a>
                          </li>

                          <li class="pager__item pager__item--forward">
                            <a
                              @click="
                                totalPageCount !== 1 && setPage(currentPage + 1)
                              "
                              class="pager__item-link"
                              :class="{
                                'pager__item-link--disabled':
                                  currentPage === totalPageCount - 1,
                              }">
                            </a>
                          </li>
                        </ul>
                      </nav>
                    </template>
                  </div>
                </template>
              </Transition>
            </div>
          </div>
          <!-- related search inputs -->
          <div
            class="search-page__related"
            v-if="term && relatedData.length > 0">
            <p>{{ t.searchRelated }}</p>
            <div class="search-page__related-items search__autocomplete">
              <div class="search__autocomplete-items">
                <template v-for="(item, idx) in relatedData" :key="idx">
                  <a
                    :href="item.link"
                    v-if="item.title !== ''"
                    class="search-page__related-item search__autocomplete-item"
                    v-html="boldString(item.title, term)">
                  </a>
                </template>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
// components
import VueScrollTo from 'vue-scrollto';
import SearchResult from './SearchResult';
// utils
import translationStrings from '../../translations/translationStrings';

export default {
  name: 'search-page',
  components: {
    SearchResult,
  },
  props: {
    term: {
      type: String,
      required: true,
    },
    search: {
      type: String,
      required: true,
    },
    related: {
      type: String,
      required: true,
    },
    lang: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      tabActive: 0,
      searchData: {},
      relatedData: [],
      currentPage: 0,
      itemsPerPage: 10,
      totalPageCount: 0,
      currentPageItems: [],
      t: translationStrings,
    };
  },
  computed: {
    activeContent() {
      return this.displayResultsPage
        ? Object.values(this.searchData)[this.tabActive].content
        : [];
    },
    displayResultsPage() {
      return this.term !== '' && this.term != null;
    },
  },
  watch: {
    tabActive(newActive, oldActive) {
      const oldTab = this.$refs.links[oldActive];
      const newTab = this.$refs.links[newActive];

      // handle green line animations
      if (oldActive < newActive) {
        oldTab.classList.add('paragraph-tabs__tab-link--to-left');
        oldTab.classList.remove('paragraph-tabs__tab-link--active');

        newTab.classList.add('paragraph-tabs__tab-link--to-right');
        setTimeout(() => {
          newTab.classList.add('paragraph-tabs__tab-link--active');
        }, 400);
      } else if (oldActive > newActive) {
        oldTab.classList.add('paragraph-tabs__tab-link--to-right');
        oldTab.classList.remove('paragraph-tabs__tab-link--active');

        newTab.classList.add('paragraph-tabs__tab-link--to-left');
        setTimeout(() => {
          newTab.classList.add('paragraph-tabs__tab-link--active');
        }, 400);
      }

      setTimeout(() => {
        [oldTab, newTab].forEach((tab) =>
          tab.classList.remove(
            'paragraph-tabs__tab-link--to-left',
            'paragraph-tabs__tab-link--to-right',
          ),
        );
      }, 400);

      this.resetPager();
    },
    currentPage() {
      VueScrollTo.scrollTo('#scroll-to', 400, {
        offset: -180,
        force: false,
      });
    },
  },
  mounted() {
    this.searchData = this.search.length > 0 ? JSON.parse(this.search) : {};
    this.relatedData = this.related.length > 0 ? JSON.parse(this.related) : [];

    this.resetPager();
  },
  methods: {
    handleScroll({ target }) {
      const container = this.$refs.linksContainer;
      if (target.offsetWidth + target.scrollLeft >= target.scrollWidth) {
        container.classList.add('scroll--end');
        container.classList.remove('scroll--start');
      } else if (target.scrollLeft === 0) {
        container.classList.add('scroll--start');
        container.classList.remove('scroll--end');
      } else {
        container.classList.add('scroll', 'scroll--end', 'scroll--start');
      }
    },
    setPage(page) {
      this.currentPage = page;

      this.currentPageItems = this.activeContent.slice(
        this.currentPage * this.itemsPerPage,
        this.currentPage * this.itemsPerPage + this.itemsPerPage,
      );
    },
    showEllipsis(page, key) {
      if (key === 0) {
        return false;
      }
      if (page === key) {
        return false;
      }
      if (page - 1 === key) {
        return false;
      }
      if (page + 1 === key) {
        return false;
      }
      if (this.totalPageCount - 1 === key) {
        return false;
      }

      return true;
    },
    resetPager() {
      this.currentPage = 0;
      this.currentPageItems = this.activeContent.slice(0, this.itemsPerPage);
      this.totalPageCount = Math.ceil(
        this.activeContent.length / this.itemsPerPage,
      );
    },
    boldString(str, substr) {
      if (!str || !substr || str.length === 0) return;
      const cleanSubstring = String(substr).replace(
        /[-[\]{}()*+!<=:?.\/\\^$|#\s,]/g,
        '\\$&',
      );
      const rgx = new RegExp(cleanSubstring, 'gi');
      return String(str).replaceAll(rgx, '<span>$&</span>');
    },
    colorTitle(string) {
      const split = string.split(' ');
      if (split.length > 1) {
        const last = split.pop();
        const rest = split.join(' ');
        return `${rest} <span>${last}</span>`;
      }

      return string;
    },
  },
};
</script>
